import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import vuetify from './plugins/vuetify'
import appComponents from "@/plugins/appComponents"
import Highcharts from 'highcharts'
import highchartsMore from "highcharts/highcharts-more"
import highcharts3d from 'highcharts/highcharts-3d'
import sankey from 'highcharts/modules/sankey'
import Annotations from "highcharts/modules/annotations"
import HighchartsVue from 'highcharts-vue'
import stockInit from 'highcharts/modules/stock'
import HighchartsXrange from 'highcharts/modules/xrange'
import HighchartsTreemap from 'highcharts/modules/treemap'
import HighchartsTreegraph from 'highcharts/modules/treegraph'
import { Field, Form, defineRule, configure } from 'vee-validate'
import VueGtag from 'vue-gtag'
import '@/styles/main.scss'
import 'moment-timezone'
import Multiselect from '@vueform/multiselect'
import VueGoogleMaps from '@fawmi/vue-google-maps'
import { UserAgentApplication, LogLevel } from "msal"

const clientId = 'a83dabf8-d5cb-407e-ae5c-25f1cd6e358b'
const tenantId = '54580ea7-f79c-4bd3-a966-99a677cc66aa'
const redirectUri = window.location.origin

const config = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    clientCapabilities: ['CP1'],
    navigateToLoginRequestUrl: false
  },
  system: {
    loggerOptions: {
      loggerCallback(logLevel, message) {
        if (logLevel <= LogLevel.Warning) {
          console.log(message)
        }
      },
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false
    }
  }
}

export const tokenConfig = {
  scopes: ["https://devportal.swaymedical.com/user_impersonation/Files.Read"]
}

export const loginRequest = {
  scopes: ["openid", "profile"]
}

export const tokenRequest = {
  scopes: ['api://your-api-client-id/.default']
}

// Check if this is a callback from Microsoft (has hash with id_token or access_token)
const isCallback = window.location.hash && (
  window.location.hash.includes('id_token') ||
  window.location.hash.includes('access_token') ||
  window.location.hash.includes('error')
)
const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/'

let auth = null

// Initialize MSAL if:
// 1. Not on login page, OR
// 2. On login page but this is a callback from Microsoft
if (!isLoginPage || isCallback) {
  auth = new UserAgentApplication(config)
  auth.handleRedirectCallback((error, response) => {
    if (error) {
      console.error('MSAL redirect error:', error)
    }
  })
} else {
  // On login page without callback - create dummy auth to prevent auto-redirect
  console.log('MSAL: Using dummy auth object on login page')
  auth = {
    getAccount: () => {
      console.log('MSAL dummy: getAccount called')
      return null
    },
    getAllAccounts: () => {
      console.log('MSAL dummy: getAllAccounts called')
      return []
    },
    loginRedirect: (request) => {
      console.log('MSAL dummy: loginRedirect called, initializing real MSAL')
      // Lazy initialize real MSAL when user clicks SSO
      const realAuth = new UserAgentApplication(config)
      realAuth.handleRedirectCallback((error) => {
        if (error) console.error('MSAL redirect error:', error)
      })
      realAuth.loginRedirect(request)
    },
    acquireTokenSilent: () => {
      console.log('MSAL dummy: acquireTokenSilent called')
      return Promise.reject(new Error('Not initialized on login page'))
    },
    acquireTokenRedirect: () => {
      console.log('MSAL dummy: acquireTokenRedirect called')
    },
    clearCache: () => {
      console.log('MSAL dummy: clearCache called')
    },
    logout: () => {
      console.log('MSAL dummy: logout called - NOT redirecting')
    }
  }
}

export { auth }

// https://www.highcharts.com/docs/getting-started/how-to-set-options
Highcharts.setOptions({
  colors: ['#1E90FF', '#50B432', '#ED561B', '#DDDF00', '#24CBE5', '#64E572',
    '#FF9655', '#FFF263', '#6AF9C4'],
  chart: {
    style: {
      fontFamily: 'SF Pro Display, Bitter,Helvetica Neue,Helvetica,Arial,sans-serif;',
      backgroundColor: 'transparent'
    },
  },
  accessibility: {
    enabled: false
  }
})

highchartsMore(Highcharts)
Annotations(Highcharts)
stockInit(Highcharts)
sankey(Highcharts)
highcharts3d(Highcharts)
HighchartsXrange(Highcharts)
HighchartsTreemap(Highcharts)
HighchartsTreegraph(Highcharts)

// Create Vue 3 app
const app = createApp(App)

// Use plugins
app.use(router)
app.use(store)
app.use(vuetify)
app.use(HighchartsVue)
app.use(appComponents)

// Google Analytics
app.use(VueGtag, {
  config: { id: 'UA-38316397-7' }
}, router)

// Google Maps
app.use(VueGoogleMaps, {
  load: {
    key: 'AIzaSyDnkW5sFvqmZPYvrU6-epJ7dwXAbDztzKY',
    libraries: 'places',
  }
})

// Register vee-validate components globally (using non-V prefix to avoid Vuetify conflicts)
app.component('VeeField', Field)
app.component('VeeForm', Form)

// Register multiselect globally
app.component('Multiselect', Multiselect)

// Mount the app
app.mount('#app')
